import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, Server, Send, Save } from "lucide-react";

const EMPTY = { smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", smtp_from: "", smtp_use_tls: true, notify_email: "" };

export default function EmailSettings() {
  const [form, setForm] = useState(EMPTY);
  const [hasPassword, setHasPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const apply = (d) => { setForm({ ...EMPTY, ...d, smtp_password: "" }); setHasPassword(d.has_password); };
  useEffect(() => { api.get("/admin/settings/email").then((r) => apply(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings/email", { ...form, smtp_port: parseInt(form.smtp_port, 10) || 587 });
      apply(r.data);
      toast.success("Paramètres email enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/settings/email/test");
      toast.success(`Email de test envoyé à ${r.data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setTesting(false);
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569]">{label}</Label>
      <Input data-testid={`email-settings-${k.replace(/_/g, "-")}`} value={form[k] ?? ""} onChange={set(k)} className="rounded-none mt-1.5 border-gray-300" {...props} />
    </div>
  );

  return (
    <div data-testid="email-settings" className="grid lg:grid-cols-2 gap-6">
      <section className="bg-white border border-gray-200 p-6 space-y-4">
        <h3 className="font-display text-lg font-semibold text-[#1B2A4A] flex items-center gap-2"><Mail className="w-5 h-5 text-[#E0C173]" /> Réception des notifications</h3>
        <p className="text-sm text-[#475569]">Adresse qui reçoit les alertes : nouvelles annonces à valider et demandes d'infos des acheteurs.</p>
        {field("notify_email", "Adresse de réception", { type: "email", placeholder: "contact@proac-transmission.fr" })}
      </section>

      <section className="bg-white border border-gray-200 p-6 space-y-4">
        <h3 className="font-display text-lg font-semibold text-[#1B2A4A] flex items-center gap-2"><Server className="w-5 h-5 text-[#E0C173]" /> Serveur SMTP</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">{field("smtp_host", "Serveur", { placeholder: "mail.votre-domaine.fr" })}</div>
          {field("smtp_port", "Port", { type: "number", placeholder: "587" })}
        </div>
        {field("smtp_user", "Identifiant", { placeholder: "contact@proac-transmission.fr", autoComplete: "off" })}
        {field("smtp_password", "Mot de passe", { type: "password", autoComplete: "new-password", placeholder: hasPassword ? "•••••••• (inchangé si vide)" : "" })}
        {field("smtp_from", "Adresse d'expédition (optionnel)", { placeholder: "PROAC Transmission <contact@proac-transmission.fr>" })}
        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <div>
            <div className="text-sm font-medium text-[#1B2A4A]">Chiffrement STARTTLS</div>
            <div className="text-xs text-[#475569]">Port 587 : activé · Port 465 : SSL automatique</div>
          </div>
          <Switch data-testid="email-settings-smtp-use-tls" checked={!!form.smtp_use_tls} onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })} />
        </div>
      </section>

      <div className="lg:col-span-2 flex flex-wrap gap-3">
        <Button data-testid="email-settings-save" onClick={save} disabled={saving} className="rounded-none bg-[#1B2A4A] hover:bg-[#2a3d63]"><Save className="w-4 h-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}</Button>
        <Button data-testid="email-settings-test" onClick={test} disabled={testing} variant="outline" className="rounded-none border-[#E0C173] text-[#1B2A4A] hover:bg-[#E0C173]/10"><Send className="w-4 h-4 mr-2" /> {testing ? "Envoi…" : "Envoyer un email de test"}</Button>
      </div>
    </div>
  );
}
